import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { listAdminActionLog } from "@/lib/admin-audit.functions";

export const Route = createFileRoute("/_authenticated/admin/action-log")({
  head: () => ({
    meta: [
      { title: "Admin Action Log — The Hospitality Reserve" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminActionLogPage,
});

type Entry = {
  id: string;
  actor_user_id: string;
  target_user_id: string | null;
  action_type: string;
  target_resource: string | null;
  target_resource_id: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
};

function AdminActionLogPage() {
  const list = useServerFn(listAdminActionLog);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    setErr(null);
    const res = await list({
      data: {
        actionType: actionType.trim() || undefined,
        targetUserId: targetUserId.trim() || undefined,
      },
    });
    if ("error" in res && res.error) setErr(res.error);
    setEntries((res.entries ?? []) as Entry[]);
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 space-y-6">
        <div>
          <Link to="/admin" className="text-sm text-muted-foreground hover:underline">
            ← Admin
          </Link>
          <h1 className="text-2xl font-semibold mt-2">Admin action log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every approve, reject, suspend, refund, or override recorded across the
            admin console.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted-foreground">Action type</label>
            <Input
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              placeholder="e.g. suspend_user"
            />
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-muted-foreground">Target user id</label>
            <Input
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              placeholder="uuid"
            />
          </div>
          <Button onClick={refresh} disabled={loading}>
            {loading ? "Loading…" : "Filter"}
          </Button>
        </div>

        {err ? (
          <p className="text-sm text-destructive">{err}</p>
        ) : null}

        <div className="overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Target</th>
                <th className="text-left px-3 py-2">Resource</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    No entries.
                  </td>
                </tr>
              ) : (
                entries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{e.action_type}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {e.actor_user_id.slice(0, 8)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {e.target_user_id ? e.target_user_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {e.target_resource ?? "—"}
                      {e.target_resource_id ? ` · ${e.target_resource_id.slice(0, 8)}` : ""}
                    </td>
                    <td className="px-3 py-2 text-xs max-w-[240px] truncate">
                      {e.reason ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">{e.ip_address ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
